import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  AcademicCapIcon,
  BookOpenIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  CloudIcon,
  ChartBarIcon,
  ArrowRightIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import {
  PageShell,
  PremiumHero,
  PremiumSection,
  SectionHeader,
  FeatureGrid,
  GlassCard,
  PremiumCTA,
} from '../premium/PremiumPageUI';

const programs = [
  {
    title: 'Full-Stack Engineering',
    description: 'Master modern web stacks—from React and Node.js to cloud deployment and DevOps pipelines.',
    icon: CpuChipIcon,
    badge: '12 Weeks',
    tags: ['React', 'Node.js', 'AWS'],
  },
  {
    title: 'Cybersecurity Academy',
    description: 'Hands-on training in threat detection, penetration testing, and zero-trust security architecture.',
    icon: ShieldCheckIcon,
    badge: '10 Weeks',
    tags: ['SOC', 'Pen Testing', 'Compliance'],
  },
  {
    title: 'Cloud & DevOps',
    description: 'Design scalable infrastructure on AWS, Azure, and Kubernetes with industry best practices.',
    icon: CloudIcon,
    badge: '8 Weeks',
    tags: ['Kubernetes', 'CI/CD', 'IaC'],
  },
  {
    title: 'Data & AI Fundamentals',
    description: 'Build data pipelines, analytics dashboards, and introductory machine learning models.',
    icon: ChartBarIcon,
    badge: '10 Weeks',
    tags: ['Python', 'ML', 'Analytics'],
  },
];

const pathways = [
  { step: '01', title: 'Assess', desc: 'Skill evaluation and personalized learning roadmap.' },
  { step: '02', title: 'Learn', desc: 'Live sessions, labs, and mentor-led project work.' },
  { step: '03', title: 'Build', desc: 'Capstone projects solving real enterprise challenges.' },
  { step: '04', title: 'Certify', desc: 'Industry-recognized certificates and career placement support.' },
];

const highlights = [
  { icon: BookOpenIcon, title: 'Industry Curriculum', description: 'Courses aligned with enterprise hiring demands and vendor certifications.' },
  { icon: AcademicCapIcon, title: 'Expert Instructors', description: 'Learn from practitioners with years of delivery experience across Nepal and beyond.' },
  { icon: PlayCircleIcon, title: 'Hybrid Learning', description: 'Flexible online and on-campus formats with recorded sessions and live labs.' },
];

export default function Academy() {
  return (
    <PageShell>
      <PremiumHero
        badge="Nisunajni Academy"
        title="Future-Ready"
        highlight="Tech Education"
        subtitle="Launch or accelerate your technology career with immersive programs designed by industry experts—combining theory, hands-on labs, and real-world projects."
        variant="education"
        stats={[
          { value: '15+', label: 'Programs' },
          { value: '500+', label: 'Graduates' },
          { value: '92%', label: 'Placement Rate' },
          { value: '24/7', label: 'Lab Access' },
        ]}
        actions={[
          { label: 'Explore Programs', primary: true, onClick: () => document.getElementById('programs')?.scrollIntoView({ behavior: 'smooth' }) },
          { label: 'Training & Consulting', href: '/training-consulting' },
        ]}
      />

      <PremiumSection id="programs">
        <SectionHeader
          badge="Learning Tracks"
          title="Academy"
          highlight="Programs"
          subtitle="Structured pathways from fundamentals to advanced specialization—with mentorship at every stage."
        />
        <FeatureGrid items={programs} columns={4} />
      </PremiumSection>

      <PremiumSection alt>
        <SectionHeader badge="How It Works" title="Your Learning" highlight="Journey" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
          {pathways.map((item, i) => (
            <motion.div
              key={item.step}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="hero-glass-card rounded-2xl p-6 text-center hover:-translate-y-1 transition-transform"
            >
              <span className="inline-flex w-12 h-12 items-center justify-center rounded-full bg-gradient-to-br from-[#2563EB] to-cyan-500 text-white text-sm font-bold mb-4 shadow-lg shadow-blue-500/25">
                {item.step}
              </span>
              <h3 className="font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
              <p className="text-sm text-slate-600 dark:text-gray-300">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </PremiumSection>

      <PremiumSection>
        <div className="grid lg:grid-cols-3 gap-6">
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <GlassCard key={h.title} className="p-8">
                <Icon className="w-10 h-10 text-[#2563EB] mb-4" />
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{h.title}</h3>
                <p className="text-sm text-slate-600 dark:text-gray-300 leading-relaxed">{h.description}</p>
              </GlassCard>
            );
          })}
        </div>
        <div className="text-center mt-12">
          <Link to="/internship" className="inline-flex items-center gap-2 text-[#2563EB] font-semibold hover:gap-3 transition-all">
            Apply for Internship <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </PremiumSection>

      <PremiumCTA
        title="Ready to Start Learning?"
        subtitle="Enroll in our next cohort or speak with an academic advisor about the right program for your goals."
        primary={{ label: 'Contact Admissions', href: '/contact' }}
        secondary={{ label: 'View All Training', href: '/training-consulting' }}
      />
    </PageShell>
  );
}
